import React, { useState } from 'react';
import propTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { useDispatch, useSelector } from 'react-redux';

import currentWorklogActions from 'store/currentWorklog/actions';
import { agoDate, clockTime } from 'helpers/_date-helpers';
import { checkUserRole } from 'helpers/_helpers';
import { dateSettingsFormat, getTimeFormat } from 'common/dateFormatConfig';
import SignaturePopover from '../../../SignaturePopover';
import SubmitModal from './components/SubmitModal';
import SignIcon from 'assets/images/sign-icon-grey.svg';
import styles from './styles';

const useStyles = makeStyles(styles);

const isAnySignature = (submission) => {
  const { signatures } = submission;
  return Boolean(signatures && (signatures.dot || signatures.contractor));
};

const SubmitSection = ({ sheet, submissions, openSnackbar, hasAccess, user }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const dateFormat = useSelector((state) => state.personalProfile.organization?.settings?.dateFormat);
  const timeFormat = useSelector((state) => state.personalProfile.organization?.settings?.timeFormat);
  const isSubmitModalOpen = useSelector((state) => state.currentWorklog.submitModal.isOpen);

  const [popover, setPopover] = useState({ anchorEl: null, submissionId: null });

  const handleOpenPopover = (submissionId) => (event) => {
    setPopover({ anchorEl: event.currentTarget, submissionId });
  };

  const handleClosePopover = () => {
    setPopover((prev) => ({ ...prev, anchorEl: null }));
  };

  const openSubmitModal = (modalType) => () => {
    dispatch(currentWorklogActions.openSubmitModal({ modalType }));
  };

  const hasSubmitAccess = () => {
    if (checkUserRole('Field Technician')) {
      return sheet.workers.some((w) => w._id === user._id);
    }
    return true;
  };

  const formatSubmissionDate = (s) => {
    const dateStr = agoDate(s.startedAt, null, dateSettingsFormat(dateFormat).dayDateSettingsFormat);
    const timeStr = clockTime(s.startedAt, getTimeFormat([], timeFormat, dateFormat).formatForTimePars);
    return `${dateStr} at ${timeStr} by ${s.username}`;
  };

  const resubmitButton = hasAccess ? (
    <Button
      variant="text"
      className={classes.submitButton}
      onClick={openSubmitModal('Resubmit')}
      disableRipple
      disableTouchRipple
    >
      Resubmit
    </Button>
  ) : null;

  const renderSignature = (s) => (
    isAnySignature(s) && (
      <>
        <Typography
          variant="body1"
          color="textSecondary"
          onClick={handleOpenPopover(s._id)}
          className={classes.signature}
        >
          <img src={SignIcon} alt="show signature" className={classes.signIcon} />
          Signature
        </Typography>
        {popover.submissionId === s._id && (
          <SignaturePopover
            anchorEl={popover.anchorEl}
            handleClosePopover={handleClosePopover}
            signatures={s.signatures}
            defaultPosition
          />
        )}
      </>
    )
  );

  if (!submissions?.length) {
    return (
      <div>
        {hasSubmitAccess() && (
          <Button
            variant="text"
            className={classes.submitButton}
            onClick={openSubmitModal('Submit')}
            disableRipple
            disableTouchRipple
          >
            Submit
          </Button>
        )}
        {isSubmitModalOpen && <SubmitModal sheet={sheet} openSnackbar={openSnackbar} />}
      </div>
    );
  }

  return (
    <div>
      <div className={classes.header}>
        <Typography variant="body1" color="textPrimary" className={classes.label}>
          Submitted:
        </Typography>
        {resubmitButton}
      </div>

      {submissions.map((s) => (
        <div key={s._id} className={classes.submissionRow}>
          <Typography variant="body1" color="textPrimary" className={classes.leftPart}>
            {formatSubmissionDate(s)}
          </Typography>
          {renderSignature(s)}
        </div>
      ))}

      {isSubmitModalOpen && <SubmitModal sheet={sheet} openSnackbar={openSnackbar} />}
    </div>
  );
};

SubmitSection.propTypes = {
  sheet: propTypes.object.isRequired,
  submissions: propTypes.array,
  openSnackbar: propTypes.func.isRequired,
  hasAccess: propTypes.bool.isRequired,
  user: propTypes.object.isRequired,
};

export default SubmitSection;
